// ─────────────────────────────────────────────────────────────────────────────
// ErrorBoundary — catches render/lifecycle throws in its subtree and shows a
// recoverable fallback instead of unmounting the whole app to a white screen.
// The codebase had none; a malformed API shape in any chart/page blanked the app.
//
// Mount one at the root (index.js) and, once the layout migrates, a second one
// inside the protected Layout so a single page crash keeps the nav shell usable.
//
// `fallback` may be a React node or a ({ error, reset }) => node render function.
// ─────────────────────────────────────────────────────────────────────────────
import { Component } from "react";
import styles from "./ErrorBoundary.module.css";

export class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Hook point for Sentry/telemetry later.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (!error) return children;
    if (fallback) {
      return typeof fallback === "function" ? fallback({ error, reset: this.reset }) : fallback;
    }

    return (
      <div className={styles.wrap} role="alert">
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden="true">⚠</div>
          <h1 className={styles.title}>Something went wrong</h1>
          <p className={styles.message}>
            {error.message || "An unexpected error occurred."}
          </p>
          <button className={styles.button} type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
